import React, { FC, useEffect, useReducer } from 'react';

import {
  DataGroupAccessAssignmentCreate,
  DataGroupListResponse,
  IdentityRoleAssignment,
} from '@ariksa/abac/api';
import { NativeResources } from '@ariksa/inventory-core';
import {
  DataAccessIdentityTypes,
  InsightV2ApiInsightV2AllRequest,
} from '@ariksa/inventory-core/api';
import { HStack, Stack } from '@chakra-ui/react';
import { each, filter, includes, isEmpty, isEqual, toArray } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  FormFieldBox,
  getProviderAlias,
  renderIconWithLabel,
  StackedHeader,
} from 'components/DataDisplay';
import { CancelButton, Form, PrimaryButton } from 'components/DataEntry';
import { IconTypes, PrincipalAccessIcon } from 'components/Icons';
import { Tabs } from 'components/Navigation';
import { Drawer } from 'components/Overlay';
import { dataPolicyIdentityProviderOptions } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/utils';

import { selectDataGroups } from '../../selectors';
import { actions } from '../../slice';

import { JitAccess } from './JitAccess';
import { PermanentAccess } from './PermanentAccess';

const normalizeAccessAssignments = (
  assignments: DataGroupAccessAssignmentCreate[],
) =>
  assignments
    .map(assignment => ({
      identity_id: assignment.identity_id,
      identity_type: assignment.identity_type,
      role_id: assignment.role_id,
      is_temporary: assignment.is_temporary,
    }))
    .sort((left, right) =>
      JSON.stringify(left).localeCompare(JSON.stringify(right)),
    );

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dataGroup: DataGroupListResponse;
}

export const ConfigureAccessToDataGroup: FC<Props> = props => {
  const { isOpen, onClose, dataGroup } = props;
  const { dataGroups, configure } = useSelector(selectDataGroups);
  const {
    authorizedIdentities,
    oktaGroups,
    users,
    roles,
    configuration,
    replaceOnDemandAccess,
    replacePermanentUserAccess,
    replacePermanentGroupAccess,
  } = configure;
  const dispatch = useDispatch();
  const firstID = uuidv4();
  const jitFirstID = uuidv4();

  const [state, updateState] = useReducer(
    (prev, next) => {
      if (typeof next === 'function') {
        // functional update
        return next(prev);
      }
      // normal object merge
      return { ...prev, ...next };
    },
    {
      identityProvider: dataPolicyIdentityProviderOptions[0],
      groupRoleOptions: [],
      jitCloudRoleOptions: [],
      cloudRoleOptions: [],
      userOptions: [],
      jitAccessAssignments: [],
      jitAccessItems: {
        [jitFirstID]: {
          id: jitFirstID,
          group: null,
          roles: [],
        },
      },
      permanentAssignments: [],
      permanentAccessItems: {
        [firstID]: {
          id: firstID,
          principalType: 'user',
          principal: null,
          roles: [],
        },
      },
    },
  );

  useEffect(() => {
    const getOptions = data =>
      map(data, o => ({
        label: o.name ?? o.resource_id,
        value: o.uuid,
      }));
    dispatch(
      actions.getRoles({
        q: {
          identityAccount: dataGroup.scim_provider_id!,
          dataAccount: dataGroup.account_id,
        },
        onSuccess: r => {
          const options = getOptions(r);
          updateState({
            cloudRoleOptions: options,
            jitCloudRoleOptions: options,
          });
        },
      }),
    );
    dispatch(
      actions.getAuthorizedIdentities({
        q: {
          identityAccount: dataGroup.scim_provider_id!,
          identityType: DataAccessIdentityTypes.OktaGroup,
        },
        onSuccess: r => {
          const options = getOptions(r);
          updateState({ groupRoleOptions: options });
        },
      }),
    );
    const oktaGroupsRequest: InsightV2ApiInsightV2AllRequest = {
      insightV2RequestBase: {
        resource_type: NativeResources.OktaGroup,
        account_id: [dataGroup.scim_provider_id!],
      },
    };
    dispatch(actions.getOktaGroups({ q: oktaGroupsRequest }));
    dispatch(
      actions.getUsers({
        q: {
          identityAccount: dataGroup.scim_provider_id!,
          identityType: DataAccessIdentityTypes.OktaUser,
        },
        onSuccess: r => {
          const options = getOptions(r);
          updateState({ userOptions: options });
        },
      }),
    );
  }, [dispatch, dataGroup]);

  useEffect(() => {
    dispatch(
      actions.getConfiguration({
        q: { dataGroupId: dataGroup.uuid },
        onSuccess: r => {
          const jitAccessAssignments: Record<string, any> = {};
          const permanentAssignments: Record<string, any> = {};
          each(r, o => {
            if (o.is_temporary) {
              const assignmentKey = o.identity_id;
              jitAccessAssignments[assignmentKey] ??= {
                id: uuidv4(),
                groupId: o.identity_id,
                roleIds: [],
              };
              jitAccessAssignments[assignmentKey].roleIds.push(o.role_id);
              return;
            }

            const principalType =
              o.identity_type === DataAccessIdentityTypes.OktaGroup
                ? 'group'
                : 'user';
            const assignmentKey = `${principalType}:${o.identity_id}`;
            permanentAssignments[assignmentKey] ??= {
              id: uuidv4(),
              principalId: o.identity_id,
              principalType,
              roleIds: [],
            };
            permanentAssignments[assignmentKey].roleIds.push(o.role_id);
          });
          updateState({
            jitAccessAssignments: toArray(jitAccessAssignments),
            permanentAssignments: toArray(permanentAssignments),
          });
        },
      }),
    );
  }, [dispatch, dataGroup]);

  useEffect(() => {
    if (isEmpty(state.jitAccessAssignments)) return;

    const jitAccessItems: Record<string, any> = {};
    each(state.jitAccessAssignments, assignment => {
      const group = state.groupRoleOptions.find(
        o => o.value === assignment.groupId,
      );
      const roles = filter(state.jitCloudRoleOptions, o =>
        includes(assignment.roleIds, o.value),
      );
      jitAccessItems[assignment.id] = {
        id: assignment.id,
        group: group ?? { value: assignment.groupId },
        roles,
      };
    });

    updateState(prev =>
      isEqual(prev.jitAccessItems, jitAccessItems)
        ? prev
        : { ...prev, jitAccessItems },
    );
  }, [
    state.groupRoleOptions,
    state.jitAccessAssignments,
    state.jitCloudRoleOptions,
    state.userOptions,
  ]);

  useEffect(() => {
    if (isEmpty(state.permanentAssignments)) return;

    const permanentAccessItems: Record<string, any> = {};
    each(state.permanentAssignments, assignment => {
      const principalOptions =
        assignment.principalType === 'group'
          ? oktaGroups.data
          : state.userOptions;
      const principal = principalOptions.find(
        o => String(o.value) === String(assignment.principalId),
      ) ?? {
        label: assignment.principalId,
        value: assignment.principalId,
      };
      const roles = filter(state.cloudRoleOptions, o =>
        includes(assignment.roleIds, o.value),
      );
      permanentAccessItems[assignment.id] = {
        id: assignment.id,
        principalType: assignment.principalType,
        principal,
        roles,
      };
    });

    updateState(prev =>
      isEqual(prev.permanentAccessItems, permanentAccessItems)
        ? prev
        : { ...prev, permanentAccessItems },
    );
  }, [
    state.cloudRoleOptions,
    state.permanentAssignments,
    state.userOptions,
    oktaGroups.data,
  ]);

  const getCurrentAssignments = () => {
    const jit: DataGroupAccessAssignmentCreate[] = [];
    each(state.jitAccessItems, o => {
      if (
        !o.group?.value ||
        !state.groupRoleOptions.some(
          option => String(option.value) === String(o.group.value),
        )
      )
        return;

      each(o.roles, (r: Record<string, any>) =>
        jit.push({
          identity_type: DataAccessIdentityTypes.OktaGroup,
          identity_id: o.group.value,
          role_id: r.value,
          is_temporary: true,
        }),
      );
    });

    const permanent: DataGroupAccessAssignmentCreate[] = [];
    each(state.permanentAccessItems, o => {
      if (!o.principal?.value) return;

      each(o.roles, (r: Record<string, any>) =>
        permanent.push({
          identity_type:
            o.principalType === 'group'
              ? DataAccessIdentityTypes.OktaGroup
              : DataAccessIdentityTypes.OktaUser,
          identity_id: o.principal.value,
          role_id: r.value,
          is_temporary: false,
        }),
      );
    });

    return [...jit, ...permanent];
  };

  const getPreviousAssignments = () =>
    [
      ...state.jitAccessAssignments.flatMap(assignment =>
        assignment.roleIds.map(roleId => ({
          identity_type: DataAccessIdentityTypes.OktaGroup,
          identity_id: assignment.groupId,
          role_id: roleId,
          is_temporary: true,
        })),
      ),
      ...state.permanentAssignments.flatMap(assignment =>
        assignment.roleIds.map(roleId => ({
          identity_type:
            assignment.principalType === 'group'
              ? DataAccessIdentityTypes.OktaGroup
              : DataAccessIdentityTypes.OktaUser,
          identity_id: assignment.principalId,
          role_id: roleId,
          is_temporary: false,
        })),
      ),
    ] as DataGroupAccessAssignmentCreate[];

  const toIdentityRoleAssignments = (
    assignments: DataGroupAccessAssignmentCreate[],
  ): IdentityRoleAssignment[] => {
    const assignmentsByIdentity = assignments.reduce<
      Record<string, IdentityRoleAssignment>
    >((result, assignment) => {
      const current =
        result[assignment.identity_id] ??
        (result[assignment.identity_id] = {
          identity_id: assignment.identity_id,
          role_ids: [],
        });

      if (!current.role_ids.includes(assignment.role_id)) {
        current.role_ids.push(assignment.role_id);
      }

      return result;
    }, {});

    return Object.values(assignmentsByIdentity);
  };

  const currentAssignments = getCurrentAssignments();
  const previousAssignments = getPreviousAssignments();
  const currentJitAssignments = currentAssignments.filter(
    assignment => assignment.is_temporary,
  );
  const currentPermanentUserAssignments = currentAssignments.filter(
    assignment =>
      !assignment.is_temporary &&
      assignment.identity_type === DataAccessIdentityTypes.OktaUser,
  );
  const currentPermanentGroupAssignments = currentAssignments.filter(
    assignment =>
      !assignment.is_temporary &&
      assignment.identity_type === DataAccessIdentityTypes.OktaGroup,
  );
  const previousJitAssignments = previousAssignments.filter(
    assignment => assignment.is_temporary,
  );
  const previousPermanentUserAssignments = previousAssignments.filter(
    assignment =>
      !assignment.is_temporary &&
      assignment.identity_type === DataAccessIdentityTypes.OktaUser,
  );
  const previousPermanentGroupAssignments = previousAssignments.filter(
    assignment =>
      !assignment.is_temporary &&
      assignment.identity_type === DataAccessIdentityTypes.OktaGroup,
  );
  const hasJitAccessChanges = !isEqual(
    normalizeAccessAssignments(currentJitAssignments),
    normalizeAccessAssignments(previousJitAssignments),
  );
  const hasPermanentUserAccessChanges = !isEqual(
    normalizeAccessAssignments(currentPermanentUserAssignments),
    normalizeAccessAssignments(previousPermanentUserAssignments),
  );
  const hasPermanentGroupAccessChanges = !isEqual(
    normalizeAccessAssignments(currentPermanentGroupAssignments),
    normalizeAccessAssignments(previousPermanentGroupAssignments),
  );
  const hasAccessChanges =
    hasJitAccessChanges ||
    hasPermanentUserAccessChanges ||
    hasPermanentGroupAccessChanges;
  const isAccessLoading =
    configuration.isLoading ||
    authorizedIdentities.isLoading ||
    users.isLoading ||
    roles.isLoading;
  const isSavingAccess =
    replaceOnDemandAccess.isLoading ||
    replacePermanentUserAccess.isLoading ||
    replacePermanentGroupAccess.isLoading;

  const onSave = () => {
    if (!hasAccessChanges || isSavingAccess) return;

    let pendingRequests = 0;
    let hasRequestFailed = false;
    const onRequestSuccess = () => {
      pendingRequests -= 1;

      if (pendingRequests !== 0 || hasRequestFailed) return;

      onClose();
      dispatch(
        actions.getDataGroups({
          q: {
            page: dataGroups.page.info.page_number,
            size: dataGroups.page.info.page_size,
          },
        }),
      );
    };
    const onRequestError = () => {
      hasRequestFailed = true;
    };
    const request = {
      dataGroupId: dataGroup.uuid,
    };

    if (hasJitAccessChanges) {
      pendingRequests += 1;
      dispatch(
        actions.replaceOnDemandAccess({
          q: {
            ...request,
            replaceAccessSliceRequest: {
              assignments: toIdentityRoleAssignments(currentJitAssignments),
            },
          },
          onSuccess: onRequestSuccess,
          onError: onRequestError,
        }),
      );
    }

    if (hasPermanentUserAccessChanges) {
      pendingRequests += 1;
      dispatch(
        actions.replacePermanentUserAccess({
          q: {
            ...request,
            replaceAccessSliceRequest: {
              assignments: toIdentityRoleAssignments(
                currentPermanentUserAssignments,
              ),
            },
          },
          onSuccess: onRequestSuccess,
          onError: onRequestError,
        }),
      );
    }

    if (hasPermanentGroupAccessChanges) {
      pendingRequests += 1;
      dispatch(
        actions.replacePermanentGroupAccess({
          q: {
            ...request,
            replaceAccessSliceRequest: {
              assignments: toIdentityRoleAssignments(
                currentPermanentGroupAssignments,
              ),
            },
          },
          onSuccess: onRequestSuccess,
          onError: onRequestError,
        }),
      );
    }
  };

  const updateJitAccessItem = (item, field, value) => {
    updateState(prev => ({
      ...prev,
      jitAccessItems: {
        ...prev.jitAccessItems,
        [item.id]: {
          ...prev.jitAccessItems[item.id],
          [field]: value,
        },
      },
    }));
  };

  const addJitAccessItem = (group: Record<string, any>) => {
    const id = uuidv4();
    updateState(prev => ({
      ...prev,
      jitAccessItems: {
        ...prev.jitAccessItems,
        [id]: { id, group, roles: [] },
      },
    }));
  };

  const removeJitAccessItem = item => {
    updateState(prev => {
      const { [item.id]: removed, ...rest } = prev.jitAccessItems;
      return { ...prev, jitAccessItems: rest };
    });
  };

  const updatePermanentAccessItems = (item, field, value) => {
    const normalizedValue =
      field === 'principal' && Array.isArray(value) ? value[0] ?? null : value;

    updateState(prev => {
      return {
        ...prev,
        permanentAccessItems: {
          ...prev.permanentAccessItems,
          [item.id]: {
            ...prev.permanentAccessItems[item.id],
            [field]: normalizedValue,
          },
        },
      };
    });
  };

  const addPermanentAccessItem = (
    principalType: 'user' | 'group',
    principal: Record<string, any>,
  ) => {
    const id = uuidv4();
    updateState(prev => ({
      ...prev,
      permanentAccessItems: {
        ...prev.permanentAccessItems,
        [id]: { id, principalType, principal, roles: [] },
      },
    }));
  };

  const removePermanentAccessItem = item => {
    updateState(prev => {
      const { [item.id]: removed, ...rest } = prev.permanentAccessItems;
      return { ...prev, permanentAccessItems: rest };
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          upper="Configure Access"
          lower={dataGroup.name}
          icon={<PrincipalAccessIcon pb={1} />}
          style={{ p: 1.5 }}
        />
      }
      body={
        <Stack spacing={5}>
          <Form
            handleSubmit={() => {}}
            schema={{
              identityProvider: {
                type: 'custom-with-form-control',
                label: 'Identity Provider (IdP)',
                component: (
                  <FormFieldBox w="50%">
                    {renderIconWithLabel(
                      dataGroup.scim_provider_type!,
                      getProviderAlias(dataGroup.scim_provider_type),
                    )}
                  </FormFieldBox>
                ),
              },
              dataGroup: {
                type: 'custom-with-form-control',
                label: 'Data Group Name',
                component: (
                  <FormFieldBox w="50%">
                    {renderIconWithLabel(IconTypes.DataGroup, dataGroup?.name)}
                  </FormFieldBox>
                ),
              },
              jitAccess1: {
                type: 'custom-with-form-control',
                component: (
                  <Tabs
                    items={[
                      {
                        key: 'on-demand-access',
                        title: 'On-Demand',
                        iconType: IconTypes.Jit,
                        styles: { iconSize: 6 },
                        component: (
                          <JitAccess
                            groupOptions={state.groupRoleOptions}
                            cloudRoleOptions={state.jitCloudRoleOptions}
                            jitAccessItems={state.jitAccessItems}
                            isGroupsLoading={authorizedIdentities.isLoading}
                            isCloudRolesLoading={roles.isLoading}
                            onAddItem={addJitAccessItem}
                            onRemoveItem={removeJitAccessItem}
                            onUpdateItem={updateJitAccessItem}
                          />
                        ),
                      },
                      {
                        key: 'permanent-access',
                        title: 'Permanent',
                        iconType: IconTypes.Infinity,
                        styles: { iconSize: 6 },
                        component: (
                          <PermanentAccess
                            userOptions={state.userOptions}
                            groupOptions={oktaGroups.data}
                            roleOptions={state.cloudRoleOptions}
                            permanentAccessItems={state.permanentAccessItems}
                            isUsersLoading={users.isLoading}
                            isGroupsLoading={oktaGroups.isLoading}
                            isRolesLoading={roles.isLoading}
                            onAddItem={addPermanentAccessItem}
                            onRemoveItem={removePermanentAccessItem}
                            onUpdateItem={updatePermanentAccessItems}
                          />
                        ),
                      },
                    ]}
                    styles={{
                      tab: {
                        stack: {
                          alignItems: 'center',
                          direction: 'row',
                          spacing: 2,
                        },
                      },
                      tabPanel: { pt: 6 },
                    }}
                  />
                ),
              },
            }}
            buttonOptions={{
              submit: {
                name: 'SAVE',
                isLoading: isSavingAccess,
                isDisabled: isSavingAccess,
                isVisible: false,
              },
            }}
            styles={{ form: { formWidth: 'full' } }}
          />
        </Stack>
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton label="CANCEL" onClick={onClose} />
          <PrimaryButton
            px={6}
            onClick={onSave}
            isLoading={isSavingAccess}
            isDisabled={isSavingAccess || isAccessLoading || !hasAccessChanges}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
      styles={{ content: { maxW: '6xl' } }}
    />
  );
};
